import { FC, useState, useMemo } from "react";
import { Badge, Modal, Table } from "@mantine/core";
import { useStyle } from "./IndividualTable.styles";
import { getKycColor } from "../../Utils/kyc";
import { capitalizeFirstLetter } from "@funded-here-interface/common/src/Utils/string";
import IndividualDetails from "../IndividualDetails/IndividualDetails";
import { useDisclosure } from "@mantine/hooks";
import { convertDate } from "@funded-here-interface/common/src/Utils/date";
import {
  KYCStatus,
  RegtankOnboardingStatusEnum,
  RegtankReattemptApprovalEnum,
} from "@funded-here-interface/common/src/constant/enum";
import { PERSONA_DISPLAY_MAP } from "@funded-here-interface/common/src/constant/constant";
import { RegtankRequest } from "@funded-here-interface/common/src/Utils/navigation/types";
import { sortRegtankRequestsByExpiry } from "@funded-here-interface/common/src/Utils/regtankOnboardingHelper";

interface IProps {
  data: any;
  refetchList: () => void;
}

interface TableColumn {
  label: string;
  className?: string;
}

const IndividualTable: FC<IProps> = ({ data, refetchList }) => {
  const { classes } = useStyle();
  const [id, setid] = useState<number>(0);
  const [opened, { open, close }] = useDisclosure(false);
  const [userId, setUserId] = useState<number | null>(null);

  // Table column configuration
  const tableColumns = useMemo<TableColumn[]>(
    () => [
      { label: "Onboarding Date" },
      { label: "First Name" },
      { label: "Last Name" },
      { label: "Email" },
      { label: "Nationality" },
      { label: "Role" },
      { label: "Regtank Reattempt Approval" },
      { label: "KYC Status" },
      { label: "Signature" },
    ],
    []
  );

  // Get signature status helper
  const getSignatureStatus = (
    declarationAISubmitted: boolean,
    regtankRequest: RegtankRequest[] | undefined,
    kycStatus: KYCStatus
  ): string => {
    let signatureStatus = "-";
    let sortedRegtankRequest: RegtankRequest[] = [];
    if (regtankRequest && regtankRequest.length > 0) {
      sortedRegtankRequest = sortRegtankRequestsByExpiry(regtankRequest);
    }
    if (declarationAISubmitted === true) {
      signatureStatus = "signed";
    } else if (
      sortedRegtankRequest?.[0]?.onboardingStatus ===
        RegtankOnboardingStatusEnum.APPROVED &&
      kycStatus === KYCStatus.PENDING_APPROVAL
    ) {
      signatureStatus = "pending";
    }
    return signatureStatus;
  };

  // Unified badge color mapping
  const getBadgeColor = (
    type: "signature" | "regtankReattempt",
    value: string
  ): string => {
    if (type === "signature") {
      switch (value) {
        case "signed":
          return "green";
        case "pending":
          return "orange";
        default:
          return "green";
      }
    }

    // regtankReattempt
    switch (value) {
      case RegtankReattemptApprovalEnum.ACTION_REQUIRED:
        return "yellow";
      case RegtankReattemptApprovalEnum.NO_ACTION_REQUIRED:
        return "gray";
      default:
        return "gray";
    }
  };

  const rows = data.map((row: any) => {
    // Extract user data to avoid repetitive access
    const user = row?.user?.[0];
    const roleName = user?.orgRole?.[0]?.role?.name;
    const displayRole = roleName
      ? PERSONA_DISPLAY_MAP[roleName] || capitalizeFirstLetter(roleName)
      : "-";
    const kycStatus = row.kycStatus;
    // Memoize signature status to avoid triple calculation
    const signatureStatus = getSignatureStatus(
      user.declarationAISubmitted,
      user?.regtankRequest,
      kycStatus
    );

    return (
      <tr
        key={row.id}
        onClick={() => {
          open();
          setid(row.id);
          setUserId(user?.id || null);
        }}
      >
        <td className={classes.td}>
          {row?.createdDate ? convertDate(row.createdDate) : "-"}
        </td>
        <td className={classes.td}>{user?.firstName || "-"}</td>
        <td className={classes.td}>{user?.lastName || "-"}</td>
        <td className={classes.td}>{user?.email || "-"}</td>
        <td className={classes.td}>{row?.nationality || "-"}</td>
        <td className={classes.td}>{displayRole}</td>
        <td>
          <Badge
            color={getBadgeColor(
              "regtankReattempt",
              row?.regtankReattemptApproval
            )}
          >
            {row?.regtankReattemptApproval}
          </Badge>
        </td>
        <td>
          <Badge color={getKycColor(row?.kycStatus)}>
            {row?.kycStatus || "-"}
          </Badge>
        </td>
        <td>
          {signatureStatus !== "-" ? (
            <Badge color={getBadgeColor("signature", signatureStatus)}>
              {signatureStatus}
            </Badge>
          ) : (
            "-"
          )}
        </td>
      </tr>
    );
  });

  return (
    <div className={classes.tableFlex}>
      <Table
        highlightOnHover
        withBorder
        withColumnBorders
        className={classes.csvTable}
      >
        <thead>
          <tr className={classes.tr}>
            {tableColumns.map((column) => (
              <th key={column.label} className={classes.th}>
                <p className={classes.thinside}>{column.label}</p>
              </th>
            ))}
          </tr>
        </thead>
        <tbody style={{ cursor: "pointer" }}>{rows}</tbody>
      </Table>

      <Modal
        opened={opened}
        onClose={close}
        size="85%"
        withCloseButton={false}
        padding="0"
      >
        <IndividualDetails
          id={id}
          close={close}
          refetchList={refetchList}
          userId={userId}
        />
      </Modal>
    </div>
  );
};

export default IndividualTable;
